import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import RendimentoModal from './components/modalServices/RendimentoModal';
import Pagination10 from './components/Pagination10';
import LoadingModal from './components/modals/LoadingModal';
import usePermissions from './components/hooks/usePermissions';
import { formatCurrencyView, formatCurrencyWithClass } from './utils/currencyUtils';
import PageCard from './components/PageCard';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';

function Rendimentos() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('rendimentos');
  
  // Verificação de situação do programa
  const { isProgramaAtivo } = useProgramaSituacao();
  
  const [rendimentos, setRendimentos] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase =
    parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  // Removido modalData, feedback é tratado no RendimentoModal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showRendimentoModal, setShowRendimentoModal] = useState(false);
  const [rendimentoToEdit, setRendimentoToEdit] = useState(null);
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.RENDIMENTO}/all`, {
        params: { cnpjAssociacao: cnpj, ano: parseInt(anoBase) },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setRendimentos(Array.isArray(result.data) ? result.data : []);
      })
      .catch(() => setRendimentos([]))
      .finally(() => setLoading(false));
  }, [atualizar, token, cnpj, anoBase, navigate, podeAbrir, carregandoPermissoes]);

  const totalItems = rendimentos.length;
  const [pagination, setPagination] = useState({
    startIndex: 0,
    rowsPerPage: 10,
    currentPage: 1,
    onGoToPage: page => {
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        startIndex: (page - 1) * prev.rowsPerPage,
      }));
    },
  });

  const selectedRendimentos = rendimentos.slice(
    pagination.startIndex,
    pagination.startIndex + pagination.rowsPerPage
  );

  const getMonthCellStyle = (value, isSelected) => {
    const baseStyle = isSelected
      ? { fontWeight: 'bold', color: '#0d6efd' }
      : {};
    if (value < 0) {
      return { ...baseStyle, color: '#dc3545', fontWeight: 'bold' };
    }
    return baseStyle;
  };

  const handleSelectRecord = record => {
    const recordKey = `${record.cnpjAssociacao}-${record.ano}-${record.numConta}`;
    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
      setIsProgramaInativo(false);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
      // Verificar se o programa está encerrado (se houver idPrograma)
      if (record.idPrograma) {
        const programaAtivo = isProgramaAtivo(record.idPrograma);
        setIsProgramaInativo(!programaAtivo);
      } else {
        setIsProgramaInativo(false);
      }
    }
  };

  const handleNewRendimento = () => {
    setRendimentoToEdit(null);
    setTimeout(() => setShowRendimentoModal(true), 0);
  };

  const handleEditRendimento = () => {
    if (selectedRecord) {
      setRendimentoToEdit(selectedRecord.data);
      setShowRendimentoModal(true);
    }
  };

  // Garante que ao fechar o modal, o registro selecionado é limpo
  const handleCloseRendimentoModal = () => {
    setShowRendimentoModal(false);
    setRendimentoToEdit(null);
    setSelectedRecord(null);
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Rendimentos"
      icon="bi-graph-up"
      scrollable={false}
      footer={rendimentos.length > 0 ? (
        <Pagination10
          startIndex={pagination.startIndex}
          rowsPerPage={pagination.rowsPerPage}
          currentPage={pagination.currentPage}
          onGoToPage={pagination.onGoToPage}
          totalItems={totalItems}
        />
      ) : null}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Rendimentos.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewRendimento}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar rendimentos' : 'Novo rendimento'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            onClick={handleEditRendimento}
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            disabled={!podeEditar || !selectedRecord || isProgramaInativo}
            title={!podeEditar ? 'Você não tem permissão para editar' : !selectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível editar' : 'Editar registro selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle">Ano</th>
                <th className="px-2 align-middle">Conta</th>
                <th className="px-2 align-middle">Programa</th>
                <th className="px-1 align-middle text-center">JAN</th>
                <th className="px-1 align-middle text-center">FEV</th>
                <th className="px-1 align-middle text-center">MAR</th>
                <th className="px-1 align-middle text-center">ABR</th>
                <th className="px-1 align-middle text-center">MAI</th>
                <th className="px-1 align-middle text-center">JUN</th>
                <th className="px-1 align-middle text-center">JUL</th>
                <th className="px-1 align-middle text-center">AGO</th>
                <th className="px-1 align-middle text-center">SET</th>
                <th className="px-1 align-middle text-center">OUT</th>
                <th className="px-1 align-middle text-center">NOV</th>
                <th className="px-1 align-middle text-center">DEZ</th>
                <th className="px-2 align-middle text-center">Total Rendimentos</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                selectedRendimentos.map(r => {
                  const recordKey = `${r.cnpjAssociacao}-${r.ano}-${r.numConta}`;
                  const isSelected =
                    selectedRecord && selectedRecord.key === recordKey;
                  return (
                    <tr
                      key={recordKey}
                      style={
                        isSelected
                          ? {
                              cursor: 'pointer',
                              backgroundColor: '#e3f2fd',
                              borderLeft: '4px solid #0d6efd',
                              boxShadow: '0 2px 4px rgba(13, 110, 253, 0.2)',
                            }
                          : { cursor: 'pointer' }
                      }
                      onClick={() => handleSelectRecord(r)}
                    >
                      <td
                        style={
                          isSelected
                            ? { fontWeight: 'bold', color: '#0d6efd' }
                            : {}
                        }
                      >
                        {r.ano}
                      </td>
                      <td
                        style={
                          isSelected
                            ? { fontWeight: 'bold', color: '#0d6efd' }
                            : {}
                        }
                      >
                        {r.numConta}
                      </td>
                      <td
                        style={
                          isSelected
                            ? { fontWeight: 'bold', color: '#0d6efd' }
                            : {}
                        }
                      >
                        {r.nomePrograma || '-'}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.janeiro, isSelected)}
                      >
                        {formatCurrencyView(r.janeiro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.fevereiro, isSelected)}
                      >
                        {formatCurrencyView(r.fevereiro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.marco, isSelected)}
                      >
                        {formatCurrencyView(r.marco)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.abril, isSelected)}
                      >
                        {formatCurrencyView(r.abril)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.maio, isSelected)}
                      >
                        {formatCurrencyView(r.maio)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.junho, isSelected)}
                      >
                        {formatCurrencyView(r.junho)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.julho, isSelected)}
                      >
                        {formatCurrencyView(r.julho)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.agosto, isSelected)}
                      >
                        {formatCurrencyView(r.agosto)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.setembro, isSelected)}
                      >
                        {formatCurrencyView(r.setembro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.outubro, isSelected)}
                      >
                        {formatCurrencyView(r.outubro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.novembro, isSelected)}
                      >
                        {formatCurrencyView(r.novembro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(r.dezembro, isSelected)}
                      >
                        {formatCurrencyView(r.dezembro)}
                      </td>
                      <td
                        className="text-end px-2"
                        style={getMonthCellStyle(
                          (r.janeiro || 0) + (r.fevereiro || 0) + (r.marco || 0) +
                          (r.abril || 0) + (r.maio || 0) + (r.junho || 0) +
                          (r.julho || 0) + (r.agosto || 0) + (r.setembro || 0) +
                          (r.outubro || 0) + (r.novembro || 0) + (r.dezembro || 0),
                          isSelected
                        )}
                      >
                        {formatCurrencyView(
                          (r.janeiro || 0) +
                          (r.fevereiro || 0) +
                          (r.marco || 0) +
                          (r.abril || 0) +
                          (r.maio || 0) +
                          (r.junho || 0) +
                          (r.julho || 0) +
                          (r.agosto || 0) +
                          (r.setembro || 0) +
                          (r.outubro || 0) +
                          (r.novembro || 0) +
                          (r.dezembro || 0)
                        )}
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && rendimentos.length === 0 && (
                <tr>
                  <td colSpan="16" className="text-center">
                    Nenhum rendimento encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
      {showRendimentoModal && (
        <RendimentoModal
          show={showRendimentoModal}
          onClose={handleCloseRendimentoModal}
          rendimento={rendimentoToEdit}
          onSaveSuccess={() => {
            handleCloseRendimentoModal();
            setAtualizar(prev => prev + 1);
          }}
        />
      )}
    </PageCard>
  );
}

export default Rendimentos;
